import { useCallback } from 'react';

import { useShopProvider } from '#provider/useShopProvider.js';
import { Cart, CartItem, useCreateCart, useCreateConsumerCheckout } from '@givebacks/api/store';
import { getErrorMessage } from '@givebacks/utils';
import { DeepPartial } from 'core';
import { notifications } from 'ui';

interface StoreCartItems extends DeepPartial<Omit<CartItem, 'options'>> {}
interface CreateCartAndCheckoutArgs {
  storeId: string;
  cartItems: StoreCartItems[];
  /** Cart Level Fields that get attached to the `cart` on send  */
  cartFields?: DeepPartial<Cart>;
  mutationArgs?: Parameters<ReturnType<typeof useCreateCart>['mutate']>[1];
}

type UseCreateCartAndCheckoutArgs = {
  onCheckoutCreated: (checkoutId: string) => void;
  onCreateCheckoutError?: (error: Error) => void;
};

const useCreateCartAndCheckout = ({
  onCheckoutCreated,
  onCreateCheckoutError,
}: UseCreateCartAndCheckoutArgs) => {
  const { provider } = useShopProvider();
  const { mutate: createCart, isPending: isCreateCartPending } = useCreateCart();
  // Create a checkout object
  const { mutate: createConsumerCheckout, isPending: isCreateConsumerCheckoutPending } =
    useCreateConsumerCheckout();

  const createCheckoutAndNavigateToCheckout = useCallback(
    (cartId: string) => {
      createConsumerCheckout(
        {
          checkout: {
            pending_cart_ids: [cartId],
          },
        },
        {
          onSuccess: (response) => {
            onCheckoutCreated(response.checkout.uuid);
          },
          onError: (error) => {
            notifications.show({
              message: getErrorMessage(error, 'Error creating checkout'),
              variant: 'error',
            });
            onCreateCheckoutError?.(error);
          },
        }
      );
    },
    [createConsumerCheckout, onCheckoutCreated, onCreateCheckoutError]
  );

  const createCartAndCheckout = useCallback(
    ({ storeId, cartItems, cartFields, mutationArgs }: CreateCartAndCheckoutArgs) => {
      createCart(
        { causeId: storeId, cart_items: cartItems, provider, ...cartFields },
        {
          ...mutationArgs,
          onSuccess: (cart, variables, context) => {
            mutationArgs?.onSuccess?.(cart, variables, context);
            createCheckoutAndNavigateToCheckout(cart.uuid);
          },
          onError: (error, variables, context) => {
            mutationArgs?.onError?.(error, variables, context);
          },
        }
      );
    },
    [createCart, createCheckoutAndNavigateToCheckout, provider]
  );

  return {
    isLoading: isCreateCartPending || isCreateConsumerCheckoutPending,
    isPending: isCreateCartPending || isCreateConsumerCheckoutPending,
    createCartAndCheckout,
  };
};

export { useCreateCartAndCheckout };
